/**
 * Привязка выбранной фичи доски к объекту каталога ОИ (core.objects).
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Crosshair } from "lucide-react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { objectsApi } from "@/services/api-v2.service";

type Props = {
  objectId: number | null;
  canManage: boolean;
  bindFromMap: boolean;
  busy?: boolean;
  onBindFromMapChange: (active: boolean) => void;
  onChangeObjectId: (objectId: number | null) => void;
};

export function TacticalObjectBindPanel({
  objectId,
  canManage,
  bindFromMap,
  busy = false,
  onBindFromMapChange,
  onChangeObjectId,
}: Props) {
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchInput), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const catalogSearchQ = useMemo(() => {
    const raw = searchDebounced.trim();
    if (!raw) return undefined;
    if (/^#\d+$/.test(raw)) return raw.slice(1);
    return raw;
  }, [searchDebounced]);

  const catalogQuery = useQuery({
    queryKey: ["tactical", "catalog-objects", catalogSearchQ ?? ""],
    queryFn: () =>
      objectsApi.list({
        limit: 50,
        status: "active",
        ...(catalogSearchQ ? { q: catalogSearchQ } : {}),
      }),
    staleTime: 30_000,
  });

  const linkedQuery = useQuery({
    queryKey: ["tactical", "object", objectId],
    queryFn: () => objectsApi.get(objectId as number),
    enabled: objectId != null,
  });

  const options = useMemo((): ComboboxOption[] => {
    const byId = new Map<number, ComboboxOption>();
    for (const obj of catalogQuery.data ?? []) {
      byId.set(obj.id, {
        value: String(obj.id),
        label: `${obj.name || "Без имени"} (#${obj.id})`,
        shortLabel: obj.name || `#${obj.id}`,
        fullLabel: obj.full_name || obj.name || undefined,
      });
    }
    const linked = linkedQuery.data;
    if (linked && !byId.has(linked.id)) {
      byId.set(linked.id, {
        value: String(linked.id),
        label: `${linked.name || "Без имени"} (#${linked.id})`,
        shortLabel: linked.name || `#${linked.id}`,
        fullLabel: linked.full_name || linked.name || undefined,
      });
    }
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, "ru"));
  }, [catalogQuery.data, linkedQuery.data]);

  const linkedName =
    linkedQuery.data?.name ||
    options.find((o) => o.value === String(objectId))?.shortLabel ||
    null;

  return (
    <div className="space-y-2 border-t pt-2">
      <div className="flex items-center gap-1 px-1">
        <p className="text-xs font-medium text-muted-foreground">Объект ОИ</p>
        <SectionHelpTooltip label="Справка: привязка к ОИ">
          <p>Связь выбранной фичи доски с объектом каталога (`core.objects`).</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Выберите объект в списке или укажите кликом по подложке ОИ на карте.</li>
            <li>«Без привязки» снимает связь; фича на доске остаётся.</li>
            <li>Esc снимает режим указания на карте.</li>
          </ul>
        </SectionHelpTooltip>
      </div>
      <div className="flex gap-1 px-1">
        <Combobox
          value={objectId != null ? String(objectId) : undefined}
          options={options}
          onValueChange={(value) => {
            if (!value) {
              onChangeObjectId(null);
              return;
            }
            const id = Number(value);
            onChangeObjectId(Number.isFinite(id) ? id : null);
          }}
          placeholder="Не привязан"
          searchPlaceholder="Поиск по имени или #id…"
          emptyText={catalogQuery.isFetching ? "Поиск…" : "Объекты не найдены"}
          clearText="Без привязки"
          allowClear
          filterMode="server"
          onSearchChange={setSearchInput}
          disabled={!canManage || bindFromMap || busy}
          className="h-8 flex-1 text-xs"
        />
        {canManage && (
          <Button
            type="button"
            size="icon"
            variant={bindFromMap ? "default" : "outline"}
            className="h-8 w-8 shrink-0"
            title={
              bindFromMap
                ? "Отменить указание на карте (Esc)"
                : "Указать объект на карте (подложка ОИ)"
            }
            aria-label={
              bindFromMap ? "Отменить указание на карте" : "Указать объект на карте"
            }
            aria-pressed={bindFromMap}
            disabled={busy}
            onClick={() => onBindFromMapChange(!bindFromMap)}
          >
            <Crosshair className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {bindFromMap && (
        <p className="px-1 text-[11px] text-muted-foreground">
          Кликните объект на карте · Esc — отмена
        </p>
      )}
      {objectId != null && linkedName && (
        <p className="px-1 text-[11px] text-muted-foreground">
          {linkedName}.{" "}
          <a className="underline" href={`/oi?objectId=${objectId}`}>
            Открыть в каталоге
          </a>
        </p>
      )}
    </div>
  );
}
